import { watch, onMounted, ref, provide } from "vue";

import type { Locale } from "~/types/locale";

export const useLayoutInit = () => {
	const { localeProperties, locale, locales } = useI18n();
	const storyblokLanguage = useStoryblokLanguage();
	const { fetchStores } = useFetchStores();
	const route = useRoute();

	const modalContent = ref<string>("");
	const modalProps = ref<{ position?: string } | null | undefined>({});
	const modalHandler = {
		open: (component: string, props?: object | null) => {
			modalContent.value = component;
			modalProps.value = props;
		},
		close: () => {
			modalContent.value = "";
			modalProps.value = {};
		}
	};

	provide("modal", { modalContent, modalProps, ...modalHandler });

	const syncNorceLocale = () => {
		const norceStore = useNorceStore();
		const previousCulture = norceStore.cultureCode;
		const previousLocale = norceStore.localeCode;
		const localeChanged =
			previousCulture !== localeProperties.value.iso ||
			previousLocale !== locale.value;

		if (previousCulture !== localeProperties.value.iso) {
			norceStore.setCultureCode(localeProperties.value.iso);
		}
		if (previousLocale !== locale.value) {
			norceStore.setLocaleCode(locale.value);
		}

		// The sales area needs nothing here. It is kept per locale, so the
		// previous store's is not what the new one reads — and clearing it at
		// this point would delete the *new* locale's, since `setLocaleCode` has
		// already run above. That is what used to lose a country chosen on the EU
		// store when the shopper visited the Swedish one and came back.
		//
		// The checkout channel is a single value and does still have to go:
		// Checkout.vue reuses an already-set one, so a channel resolved for the
		// previous locale would be used for this one. Skip the "" → "<locale>"
		// first load, where there is nothing stale yet.
		if (localeChanged && previousCulture) {
			norceStore.setCheckoutChannel(undefined);
		}
	};

	const initLayoutData = async () => {
		const { initApp } = useInitApp();
		syncNorceLocale();
		await fetchStores();
		await initApp(
			localeProperties.value.iso,
			locale.value,
			// i18n's runtime locale objects carry code/iso/name but not the
			// env-only fields of `Locale` (isDefault, applicationId, …), which
			// initApp doesn't read.
			locales.value as unknown as Locale[],
			storyblokLanguage.value
		);
	};

	watch(locale, async () => {
		await initLayoutData();
	});

	watch(
		() => route.fullPath,
		async (_newPath, oldPath) => {
			const norceStore = useNorceStore();
			const stale =
				norceStore.cultureCode !== localeProperties.value.iso ||
				norceStore.localeCode !== locale.value;
			if (!stale) return;
			syncNorceLocale();
			if (oldPath !== undefined) {
				await initLayoutData();
			}
		}
	);

	onMounted(() => {
		if (userStore()?.getUser) {
			useWishlistStore().initWishlist();
		}
	});

	return { initLayoutData };
};
